// Probably need an ID gotten off the route, useParams mayhaps?

import { Box, FormControlLabel, Grid, Paper, styled, Switch, Typography, useTheme } from "@mui/material";
import { GetFieldLookupItem, useFormMetadata, useLookups } from "@ngt/forms";
import { DateTime } from "luxon";
import { ChangeEvent, FC, useContext, useMemo } from "react";
import { Link, Outlet, useParams } from "react-router-dom";
import { ApiEnabledStatus, FormDefinitions, LotSummary, Permissions } from "../api/dtos";
import {
    DetailsContent,
    DetailsHeader,
    DetailsPanel,
    GridColumn,
    GridData,
    GridFillerRow,
    GridLabel,
    GridRow,
} from "../components/DetailsPanel";
import GridActions from "../components/GridActions";
import SetLinks from "../components/layout/SetLinks";
import StageAttachments from "../components/StageAttachments";
import { StyledButton } from "../components/StyledButton";
import CollapsibleAppTable from "../components/tables/CollapsibleAppTable";
import type { AppColumn } from "../components/tables/AppTable.types";
import TitleBox from "../components/TitleBox";
import { StyledLink } from "../components/utility/StyledLink";
import PermissionsContext from "../contexts/auth/PermissionsContext";
import ProjectContext from "../contexts/data/ProjectContext";
import {
    DefaultLotCreateLink,
    DefaultProjectCreateLink,
    DefaultStageCreateLink,
} from "../contexts/layouts/LinkContext";
import LotSummariesContext from "../contexts/LotSummariesContext";
import StageAttachmentsContext from "../contexts/StageAttachmentsContext";
import StageDetailsContext from "../contexts/StageDetailsContext";
import useApiActivityStatus from "../hooks/data/useApiActivityStatusByStageId";
import useAttachmentsByStageId from "../hooks/data/useAttachmentsByStageId";
import useLotSummariesByStageId from "../hooks/data/useLotSummariesByStageId";
import usePostApiStatusUpdate from "../hooks/data/usePostApiStatusUpdate";
import useStageDetailsByProjectIdStageId from "../hooks/data/useStageDetailsByProjectIdStageId";

const StyledPaper = styled(Paper)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const formatCurrency = (value: number) =>
    Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
    }).format(value);
export const toLocalCalendarDate = (value?: string | null) => {
    if (value === undefined || value === null || value === "") {
        return value;
    }

    const date = DateTime.fromISO(value);

    return date.isValid ? (date.toISODate() ?? value) : value;
};

const useLotColumns = (includeSubstages?: boolean): readonly AppColumn<LotSummary>[] => {
    const { data: permissions } = useContext(PermissionsContext);

    const metadata = useFormMetadata(FormDefinitions.Lot);

    const { data: lookups, loading: _lookupsLoading } = useLookups(metadata);

    const projectId = useParams().projectId;
    const stageId = useParams().stageId;

    const lotColumns = useMemo(() => {
        const columns: AppColumn<LotSummary>[] = [
            {
                id: "lotNo",
                label: "Lot No.",
                scalarType: "number",
                accessor: (row) => row.lotNo,
                width: 90,
                cell: ({ row, formatted }) => (
                    <StyledLink to={`/projects/${projectId}/stages/${stageId}/lots/${row.id}`}>{formatted}</StyledLink>
                ),
            },
            {
                id: "status",
                label: "Sales Status",
                scalarType: "lookup",
                accessor: (row) => row.status,
                lookup: {
                    labelFor: (value) =>
                        GetFieldLookupItem(lookups, "Status", value)?.value?.toString() ?? String(value),
                },
                sortAccessor: (row) =>
                    GetFieldLookupItem(lookups, "Status", row.status)?.value?.toString() ?? String(row.status),
                sortScalarType: "text",
                minWidth: 125,
                flex: 1,
            },
            {
                id: "listPrice",
                label: "List Price",
                scalarType: "number",
                accessor: (row) => row.listPrice,
                align: "left",
                aggregate: "sum",
                aggregateFormatter: formatCurrency,
                formatter: (value) => (typeof value === "number" ? formatCurrency(value) : ""),
                minWidth: 100,
                flex: 1,
            },
            {
                id: "contractPrice",
                label: "Contract Price",
                scalarType: "number",
                accessor: (row) => row.contractPrice,
                align: "left",
                aggregate: "sum",
                aggregateFormatter: formatCurrency,
                formatter: (value) => (typeof value === "number" ? formatCurrency(value) : ""),
                minWidth: 105,
                flex: 1,
            },
            {
                id: "contractCount",
                label: "Contract Count",
                scalarType: "number",
                accessor: (row) => row.contractCount,
                align: "left",
                aggregate: "sum",
                width: 110,
            },
            {
                id: "contractIssueDate",
                label: "Contract Issue Date",
                scalarType: "date",
                accessor: (row) => toLocalCalendarDate(row.contractIssueDate),
                width: 110,
                formatter: (value) =>
                    typeof value === "string" && value ? DateTime.fromISO(value).toFormat("dd/MM/yyyy") : "",
            },
            {
                id: "contractExchangeDate",
                label: "Contract Exchange Date",
                scalarType: "date",
                accessor: (row) => toLocalCalendarDate(row.contractExchangeDate),
                width: 120,
                formatter: (value) =>
                    typeof value === "string" && value ? DateTime.fromISO(value).toFormat("dd/MM/yyyy") : "",
            },
            {
                id: "rateAdjustments",
                label: "Rate Adjustments",
                scalarType: "number",
                accessor: (row) => row.rateAdjustments,
                align: "left",
                aggregate: "sum",
                aggregateFormatter: formatCurrency,
                formatter: (value) => (typeof value === "number" ? formatCurrency(value) : ""),
                width: 135,
            },
            {
                id: "legalFees",
                label: "Legal Fees",
                scalarType: "number",
                accessor: (row) => row.legalFees,
                align: "left",
                aggregate: "sum",
                aggregateFormatter: formatCurrency,
                formatter: (value) => (typeof value === "number" ? formatCurrency(value) : ""),
                minWidth: 100,
                flex: 1,
            },
            {
                id: "commission",
                label: "Commission",
                scalarType: "number",
                accessor: (row) => row.commission,
                align: "left",
                aggregate: "sum",
                aggregateFormatter: formatCurrency,
                formatter: (value) => (typeof value === "number" ? formatCurrency(value) : ""),
                minWidth: 135,
                flex: 1,
            },
            {
                id: "agentName",
                label: "Agent",
                scalarType: "text",
                accessor: (row) => row.agentName,
                minWidth: 100,
                flex: 1,
            },
            {
                id: "settlementDate",
                label: "Settlement Date",
                scalarType: "date",
                accessor: (row) => toLocalCalendarDate(row.settlementDate),
                width: 125,
                formatter: (value) =>
                    typeof value === "string" && value ? DateTime.fromISO(value).toFormat("dd/MM/yyyy") : "",
            },
            {
                id: "settlementProceeds",
                label: "Settlement Proceeds",
                scalarType: "number",
                accessor: (row) => row.settlementProceeds,
                align: "left",
                aggregate: "sum",
                aggregateFormatter: formatCurrency,
                formatter: (value) => (typeof value === "number" ? formatCurrency(value) : ""),
                minWidth: 125,
                flex: 1,
            },
        ];

        if (includeSubstages) {
            columns.push({
                id: "subStage",
                label: "Sub Stage",
                headerLabel: "",
                scalarType: "lookup",
                accessor: (row) => row.subStage,
                width: 30,
                lookup: {
                    labelFor: (value) =>
                        GetFieldLookupItem(lookups, "SubStage", value)?.value?.toString() ?? String(value),
                },
            });
        }

        if (
            permissions?.includes(Permissions.StageCanEditLot) ||
            permissions?.includes(Permissions.StageCanDeleteLot)
        ) {
            columns.push({
                id: "actions",
                label: "Actions",
                headerLabel: "",
                kind: "action",
                scalarType: "text",
                accessor: (row) => String(row.id ?? ""),
                width: 90,
                cell: ({ row }) =>
                    row.id == null ? null : (
                        <GridActions
                            editPermission={Permissions.StageCanEditLot}
                            deletePermission={Permissions.StageCanDeleteLot}
                            editUrl={`/projects/${projectId}/stages/${stageId}/lots/edit/${row.id}`}
                            deleteUrl={`/projects/${projectId}/stages/${stageId}/lots/delete/${row.id}`}
                        />
                    ),
            });
        }

        return columns;
    }, [lookups, permissions, includeSubstages, projectId, stageId]);

    return lotColumns;
};

export const StageDetails: FC = () => {
    const theme = useTheme();

    const { data: permissions } = useContext(PermissionsContext);

    const params = useParams();

    const projectId = Number(params.projectId);
    const stageId = Number(params.stageId);

    const { data: project } = useContext(ProjectContext);

    const { data: stageApiStatus, refresh: refreshActivityStatus } = useApiActivityStatus(
        stageId,
        FormDefinitions.Stage,
    );

    const stageAttachmentsContextData = useAttachmentsByStageId(stageId);

    const stageDetailsContextData = useStageDetailsByProjectIdStageId(projectId, stageId);
    const { data: stageData, loading: _stageDataLoading } = stageDetailsContextData;

    if (stageData?.stage && !stageData.stage.name) {
        stageData.stage.name = "Unnamed Stage";
    }

    const lotSummariesContextData = useLotSummariesByStageId(stageId);
    const { data: lotSummaries, loading: lotsLoading } = lotSummariesContextData;

    const columns = useLotColumns(!!lotSummaries?.find((ls) => ls.subStage != null));

    const updateApiStatus = usePostApiStatusUpdate();
    //const {data: lotSummaries, loading: lotsLoading} = useLotSummariesByStageId(stageId);

    const handleApiToggle = async (event: ChangeEvent<HTMLInputElement>) => {
        await updateApiStatus(stageId, FormDefinitions.Stage, event.target.checked);
        refreshActivityStatus();
    };

    return (
        //Properties

        //Summary

        //StageList
        <>
            <StageDetailsContext.Provider value={stageDetailsContextData}>
                <LotSummariesContext.Provider value={lotSummariesContextData}>
                    <StageAttachmentsContext.Provider value={stageAttachmentsContextData}>
                        <Outlet />

                        <SetLinks
                            createProjectLink={DefaultProjectCreateLink}
                            createStageLink={DefaultStageCreateLink}
                            createLotLink={DefaultLotCreateLink}
                        />

                        <TitleBox>
                            <Typography variant="h2" marginBottom={theme.spacing(2)}>
                                {stageData?.projectName
                                    ? `${stageData?.projectName} - ${stageData?.stage?.name}`
                                    : stageData?.stage?.name}
                            </Typography>
                            {permissions?.includes(Permissions.AdminAccess) && (
                                <Grid>
                                    <FormControlLabel
                                        label={
                                            "Enable Api" +
                                            (stageApiStatus == ApiEnabledStatus.Active ? ` - Id: ${stageId}` : "")
                                        }
                                        control={
                                            <Switch
                                                checked={stageApiStatus == ApiEnabledStatus.Active}
                                                disabled={stageApiStatus == ApiEnabledStatus.Unused}
                                                onChange={handleApiToggle}
                                            />
                                        }
                                    />
                                </Grid>
                            )}
                            <Grid container gap={theme.spacing(2)} justifyContent={"end"}>
                                {permissions?.includes(Permissions.ProjectCanEditStage) && (
                                    <StyledButton
                                        variant="contained"
                                        //@ts-expect-error
                                        component={Link}
                                        to={`/projects/${projectId}/stages/${stageId}/edit`}
                                        state={{
                                            back: true,
                                        }}
                                    >
                                        Edit Stage
                                    </StyledButton>
                                )}
                                {permissions?.includes(Permissions.StageCanAddLot) && (
                                    <StyledButton
                                        variant="contained"
                                        //@ts-expect-error
                                        component={Link}
                                        to={`/projects/${projectId}/stages/${stageId}/lots/create`}
                                        state={{
                                            back: true,
                                        }}
                                    >
                                        Add Lot
                                    </StyledButton>
                                )}
                                {permissions?.includes(Permissions.StageCanAddAttachment) && (
                                    <StyledButton
                                        variant="contained"
                                        //@ts-expect-error
                                        component={Link}
                                        to={`/projects/${projectId}/stages/${stageId}/attachments/create`}
                                        state={{
                                            back: true,
                                        }}
                                    >
                                        Add Attachment
                                    </StyledButton>
                                )}
                                {permissions?.includes(Permissions.StageCanStylePricelist) && (
                                    <StyledButton
                                        variant="contained"
                                        //@ts-expect-error
                                        component={Link}
                                        to={`/projects/${projectId}/stages/${stageId}/style-pricelist`}
                                        state={{
                                            back: true,
                                        }}
                                    >
                                        Style Pricelist
                                    </StyledButton>
                                )}
                                {permissions?.includes(Permissions.StageCanCreatePricelist) && (
                                    <StyledButton
                                        variant="contained"
                                        //@ts-expect-error
                                        component={Link}
                                        to={`/projects/${projectId}/stages/${stageId}/download-pricelist`}
                                        state={{
                                            back: true,
                                        }}
                                    >
                                        Download Pricelist
                                    </StyledButton>
                                )}
                                {permissions?.includes(Permissions.StageCanEditStagePlan) && (
                                    <StyledButton
                                        variant="contained"
                                        //@ts-expect-error
                                        component={Link}
                                        to={`/projects/${projectId}/stages/${stageId}/stage-plan/edit`}
                                        state={{
                                            back: true,
                                        }}
                                    >
                                        Edit Stage Plan
                                    </StyledButton>
                                )}
                                {permissions?.includes(Permissions.StageCanEditStagePlan) && (
                                    <StyledButton
                                        variant="contained"
                                        //@ts-expect-error
                                        component={Link}
                                        to={`/projects/${projectId}/stages/${stageId}/stage-plan`}
                                        state={{
                                            back: true,
                                        }}
                                    >
                                        Preview Stage Plan
                                    </StyledButton>
                                )}
                                {permissions?.includes(Permissions.StageCanBulkEditLotProperties) && (
                                    <StyledButton
                                        variant="contained"
                                        //@ts-expect-error
                                        component={Link}
                                        to={`/projects/${projectId}/stages/${stageId}/bulk-edit-lot-properties`}
                                        state={{
                                            back: true,
                                        }}
                                    >
                                        Bulk Edit Lot Properties
                                    </StyledButton>
                                )}
                                {permissions?.includes(Permissions.StageCanBulkEditLots) && (
                                    <StyledButton
                                        variant="contained"
                                        //@ts-expect-error
                                        component={Link}
                                        to={`/projects/${projectId}/stages/${stageId}/bulk-edit-lots`}
                                        state={{
                                            back: true,
                                        }}
                                    >
                                        Bulk Edit Lots
                                    </StyledButton>
                                )}
                                {permissions?.includes(Permissions.StageCanBulkEditContracts) && (
                                    <StyledButton
                                        variant="contained"
                                        //@ts-expect-error
                                        component={Link}
                                        to={`/projects/${projectId}/stages/${stageId}/bulk-edit-contracts`}
                                        state={{
                                            back: true,
                                        }}
                                    >
                                        Bulk Edit Contracts
                                    </StyledButton>
                                )}
                            </Grid>
                        </TitleBox>

                        <Grid
                            container
                            size={{
                                xs: 12,
                            }}
                            spacing={{
                                xs: 2,
                            }}
                        >
                            <Grid
                                size={{
                                    xs: 12,
                                    lg: 6,
                                }}
                            >
                                <DetailsPanel>
                                    <DetailsHeader>Lot Summary</DetailsHeader>
                                    <DetailsContent>
                                        <GridColumn>
                                            <GridRow>
                                                <GridLabel>Total Lots:</GridLabel>
                                                <GridData>{stageData?.totalLots}</GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>Coming Soon:</GridLabel>
                                                <GridData>{stageData?.comingSoon}</GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>On Hold:</GridLabel>
                                                <GridData>{stageData?.onHold}</GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>Available</GridLabel>
                                                <GridData>{stageData?.available}</GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>Sales Advice Issued:</GridLabel>
                                                <GridData>{stageData?.salesAdviceIssued}</GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>Contracts Issued:</GridLabel>
                                                <GridData>{stageData?.contractsIssued}</GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>Contracts Exchanged:</GridLabel>
                                                <GridData>{stageData?.contractsExchanged}</GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>Contracts Settled:</GridLabel>
                                                <GridData>{stageData?.contractsSettled}</GridData>
                                            </GridRow>
                                            <GridFillerRow />
                                        </GridColumn>
                                    </DetailsContent>
                                </DetailsPanel>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    lg: 6,
                                }}
                                sx={{
                                    display: "flex",
                                    alignItems: "stretch",
                                }}
                            >
                                <DetailsPanel>
                                    <DetailsHeader>Financial Summary</DetailsHeader>
                                    <DetailsContent>
                                        <GridColumn>
                                            <GridRow>
                                                <GridLabel>Total List Price:</GridLabel>
                                                <GridData>
                                                    {stageData?.totalListPrice
                                                        ? Intl.NumberFormat("en-AU", {
                                                              style: "currency",
                                                              currency: "AUD",
                                                          }).format(stageData?.totalListPrice)
                                                        : undefined}
                                                </GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>Average List Price:</GridLabel>
                                                <GridData>
                                                    {stageData?.averageListPrice
                                                        ? Intl.NumberFormat("en-AU", {
                                                              style: "currency",
                                                              currency: "AUD",
                                                          }).format(stageData?.averageListPrice)
                                                        : undefined}
                                                </GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>Total Settled Contract Price:</GridLabel>
                                                <GridData>
                                                    {stageData?.totalSettledContractPrice
                                                        ? Intl.NumberFormat("en-AU", {
                                                              style: "currency",
                                                              currency: "AUD",
                                                          }).format(stageData?.totalSettledContractPrice)
                                                        : undefined}
                                                </GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>Average Settled Contract Price:</GridLabel>
                                                <GridData>
                                                    {stageData?.averageSettledContractPrice
                                                        ? Intl.NumberFormat("en-AU", {
                                                              style: "currency",
                                                              currency: "AUD",
                                                          }).format(stageData?.averageSettledContractPrice)
                                                        : undefined}
                                                </GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>Total Unsettled Contract Price:</GridLabel>
                                                <GridData>
                                                    {stageData?.totalUnsettledContractPrice
                                                        ? Intl.NumberFormat("en-AU", {
                                                              style: "currency",
                                                              currency: "AUD",
                                                          }).format(stageData?.totalUnsettledContractPrice)
                                                        : undefined}
                                                </GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>Average Unsettled Contract Price:</GridLabel>
                                                <GridData>
                                                    {stageData?.averageUnsettledContractPrice
                                                        ? Intl.NumberFormat("en-AU", {
                                                              style: "currency",
                                                              currency: "AUD",
                                                          }).format(stageData?.averageUnsettledContractPrice)
                                                        : undefined}
                                                </GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>Total Exchanged Contract Price:</GridLabel>
                                                <GridData>
                                                    {stageData?.totalExchangedContractPrice
                                                        ? Intl.NumberFormat("en-AU", {
                                                              style: "currency",
                                                              currency: "AUD",
                                                          }).format(stageData?.totalExchangedContractPrice)
                                                        : undefined}
                                                </GridData>
                                            </GridRow>
                                            <GridFillerRow />
                                        </GridColumn>
                                    </DetailsContent>
                                </DetailsPanel>
                            </Grid>
                        </Grid>
                        <StyledPaper>
                            <Box
                                sx={{
                                    marginTop: theme.spacing(2),
                                }}
                            >
                                <CollapsibleAppTable
                                    title="Lots"
                                    startCollapsed={false}
                                    loading={lotsLoading}
                                    columns={columns}
                                    rows={lotSummaries ?? []}
                                    getRowId={(lot) => {
                                        if (lot.id == null) {
                                            throw new Error("Stage lot summary is missing its domain ID");
                                        }

                                        return lot.id;
                                    }}
                                    density="compact"
                                    pagination={{}}
                                    columnControls={{
                                        menu: true,
                                    }}
                                    filterControls
                                    export={{
                                        fileName: `${project?.name} - ${stageData?.stage?.name}`,
                                        exportTitle: `${project?.name} - ${stageData?.stage?.name}`,
                                        includeDateTime: true,
                                    }}
                                />
                            </Box>
                        </StyledPaper>
                        <StageAttachments stageId={stageId} />
                    </StageAttachmentsContext.Provider>
                </LotSummariesContext.Provider>
            </StageDetailsContext.Provider>
        </>
    );
};
